import { SITE_URL } from "@/lib/founders/content";

export type JsonLdNode = Record<string, unknown>;
export type BreadcrumbItem = { name: string; href: string };
export type ListedItem = { name: string; url: string; description?: string };
export type FaqEntry = { question: string; answer: string };
export type HowToStepEntry = { name: string; text: string };
export type OfferEntry = { name: string; price: number; url?: string };

const compact = (node: JsonLdNode): JsonLdNode =>
  Object.fromEntries(Object.entries(node).filter(([, value]) => value !== undefined));

export const absoluteUrl = (href: string): string =>
  href.startsWith("http") ? href : `${SITE_URL}${href}`;

export const breadcrumbList = (items: BreadcrumbItem[]): JsonLdNode => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.href),
  })),
});

export const itemList = (items: ListedItem[]): JsonLdNode => ({
  "@type": "ItemList",
  itemListElement: items.map((item, index) =>
    compact({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      url: absoluteUrl(item.url),
      description: item.description,
    }),
  ),
});

export const webPage = (o: {
  url: string;
  name: string;
  description: string;
  type?: "WebPage" | "CollectionPage";
  inLanguage?: string;
}): JsonLdNode => ({
  "@type": o.type ?? "WebPage",
  url: absoluteUrl(o.url),
  name: o.name,
  description: o.description,
  inLanguage: o.inLanguage ?? "en-US",
  isPartOf: { "@id": `${SITE_URL}/#website` },
});

export const collectionPage = (o: {
  url: string;
  name: string;
  description: string;
  items: ListedItem[];
}): JsonLdNode => ({
  ...webPage({ url: o.url, name: o.name, description: o.description, type: "CollectionPage" }),
  mainEntity: itemList(o.items),
});

export const offer = (o: OfferEntry): JsonLdNode =>
  compact({
    "@type": "Offer",
    name: o.name,
    price: o.price,
    priceCurrency: "USD",
    url: o.url ? absoluteUrl(o.url) : undefined,
  });

export const aggregateOffer = (o: {
  lowPrice: number;
  highPrice: number;
  offerCount: number;
  url?: string;
  offers?: JsonLdNode[];
}): JsonLdNode =>
  compact({
    "@type": "AggregateOffer",
    lowPrice: o.lowPrice,
    highPrice: o.highPrice,
    offerCount: o.offerCount,
    priceCurrency: "USD",
    url: o.url ? absoluteUrl(o.url) : undefined,
    offers: o.offers,
  });

export const softwareApplication = (o: {
  name: string;
  url: string;
  description: string;
  image?: string;
  offers?: JsonLdNode | JsonLdNode[];
}): JsonLdNode =>
  compact({
    "@type": "SoftwareApplication",
    name: o.name,
    url: absoluteUrl(o.url),
    description: o.description,
    applicationCategory: "MultimediaApplication",
    operatingSystem: "Web",
    image: o.image ? absoluteUrl(o.image) : undefined,
    offers: o.offers,
  });

export const mobileApplication = (o: {
  name: string;
  url: string;
  installUrl: string;
  description: string;
  operatingSystem: string;
  image?: string;
  datePublished?: string;
}): JsonLdNode =>
  compact({
    "@type": "MobileApplication",
    "@id": `${absoluteUrl(o.url)}#app`,
    name: o.name,
    url: absoluteUrl(o.url),
    installUrl: o.installUrl,
    sameAs: o.installUrl,
    description: o.description,
    applicationCategory: "MultimediaApplication",
    operatingSystem: o.operatingSystem,
    image: o.image ? absoluteUrl(o.image) : undefined,
    datePublished: o.datePublished,
    offers: { "@type": "Offer", price: 0, priceCurrency: "USD", url: o.installUrl },
    author: { "@id": `${SITE_URL}/#organization` },
    publisher: { "@id": `${SITE_URL}/#organization` },
  });

export const faqPage = (items: FaqEntry[]): JsonLdNode => ({
  "@type": "FAQPage",
  mainEntity: items.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
});

export const howTo = (o: {
  name: string;
  description?: string;
  steps: HowToStepEntry[];
}): JsonLdNode =>
  compact({
    "@type": "HowTo",
    name: o.name,
    description: o.description,
    step: o.steps.map((step, index) => ({
      "@type": "HowToStep",
      position: index + 1,
      name: step.name,
      text: step.text,
    })),
  });

export const person = (o: { name: string; url: string; id?: string }): JsonLdNode =>
  compact({
    "@type": "Person",
    "@id": o.id,
    name: o.name,
    url: absoluteUrl(o.url),
  });

export const blogPosting = (o: {
  url: string;
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: JsonLdNode;
  section?: string;
  keywords?: string[];
  wordCount?: number;
  timeRequired?: string;
}): JsonLdNode =>
  compact({
    "@type": "BlogPosting",
    mainEntityOfPage: { "@type": "WebPage", "@id": absoluteUrl(o.url) },
    url: absoluteUrl(o.url),
    headline: o.headline,
    description: o.description,
    image: o.image,
    datePublished: o.datePublished,
    dateModified: o.dateModified,
    author: o.author,
    publisher: {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "Crayo",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
    },
    isPartOf: { "@id": `${SITE_URL}/#website` },
    inLanguage: "en-US",
    articleSection: o.section,
    keywords: o.keywords?.length ? o.keywords : undefined,
    wordCount: o.wordCount,
    timeRequired: o.timeRequired,
    isAccessibleForFree: true,
  });

export const article = (o: {
  url: string;
  headline: string;
  description: string;
  datePublished: string;
  dateModified: string;
  image?: string;
  section?: string;
  keywords?: string[];
}): JsonLdNode =>
  compact({
    "@type": "Article",
    "@id": `${absoluteUrl(o.url)}#article`,
    mainEntityOfPage: { "@id": `${absoluteUrl(o.url)}#webpage` },
    url: absoluteUrl(o.url),
    headline: o.headline,
    description: o.description,
    image: o.image,
    datePublished: o.datePublished,
    dateModified: o.dateModified,
    author: { "@id": `${SITE_URL}/#organization` },
    publisher: { "@id": `${SITE_URL}/#organization` },
    isPartOf: { "@id": `${SITE_URL}/#website` },
    inLanguage: "en-US",
    articleSection: o.section,
    keywords: o.keywords?.length ? o.keywords : undefined,
    isAccessibleForFree: true,
  });

export const definedTerm = (o: {
  name: string;
  description: string;
  url: string;
  setUrl: string;
}): JsonLdNode => ({
  "@type": "DefinedTerm",
  name: o.name,
  description: o.description,
  url: absoluteUrl(o.url),
  inDefinedTermSet: { "@id": `${absoluteUrl(o.setUrl)}#termset` },
});

export const definedTermSet = (o: {
  name: string;
  description: string;
  url: string;
  terms: ListedItem[];
}): JsonLdNode => ({
  "@type": "DefinedTermSet",
  "@id": `${absoluteUrl(o.url)}#termset`,
  name: o.name,
  description: o.description,
  url: absoluteUrl(o.url),
  hasDefinedTerm: o.terms.map((term) => ({
    "@type": "DefinedTerm",
    name: term.name,
    url: absoluteUrl(term.url),
  })),
});

export const webApi = (o: {
  name: string;
  description: string;
  documentation: string;
}): JsonLdNode => ({
  "@type": "WebAPI",
  name: o.name,
  description: o.description,
  documentation: absoluteUrl(o.documentation),
  termsOfService: absoluteUrl("/tos"),
  provider: { "@id": `${SITE_URL}/#organization` },
});
